import ReactDOM from 'react-dom';
import React from 'react';
import {Base64} from 'js-base64';
import BTCookies from '../../common/cookies';

import {fetchIpData} from '../../vendor/ipData';
import CCPAConsentBar from './consentBar';
import uspString from '../iab/usPrivacyString';
import {trackersPush} from '../utils';

const REGION_KEY = 'bt.location.region';
const CONSENT_KEY = 'bt.cookieConsent.optedIn';
export const CACHED_LOCATION_COOKIE = '__btl';


/**
 * @typedef {Object} ConsentRequirementResponse
 * @property {boolean} isConsentRequired
 * @property {boolean} hasConsented
 * @property {boolean} hasPreviouslyResponded
 * @property {IPDataResponse|null} locationData
 */

/**
 * Checks if consent is required from the current visitor to allow tracking for personalized marketing.
 *
 * @param settings
 * @returns Promise<ConsentRequirementResponse>
 */
export async function checkConsentRequirements(settings) {
  /* eslint-disable object-curly-newline */
  const {
    consentBar: {
      enabledRegions: consentBarEnabledRegions = [],
    } = {},
  } = settings;
  /* eslint-enable object-curly-newline */

  let isConsentRequired;
  let ipInfo = null;

  const savedLocation = window.localStorage.getItem(REGION_KEY);

  if (typeof savedLocation === 'string') {
    isConsentRequired = consentBarEnabledRegions.includes(savedLocation);
    // eslint-disable-next-line max-len
    const cachedLocationB64 = BTCookies.get(CACHED_LOCATION_COOKIE); // window.localStorage.getItem(CACHED_LOCATION_KEY);
    if (typeof cachedLocationB64 === 'string') {
      try {
        const locationJson = Base64.decode(cachedLocationB64);
        ipInfo = JSON.parse(locationJson);
      } catch { /* Ignore errors parsing saved location data, it will remain null instead. */ }
    }
  } else {
    ipInfo = await fetchIpData();
    const {region_code: regionCode} = ipInfo || {};
    window.localStorage.setItem(REGION_KEY, regionCode);
    if (ipInfo) {
      BTCookies.set(CACHED_LOCATION_COOKIE, Base64.encode(JSON.stringify(ipInfo)), {expires: 30});
    }
    isConsentRequired = consentBarEnabledRegions.includes(regionCode);
  }

  const savedOptIn = window.localStorage.getItem(CONSENT_KEY) || BTCookies.get(CONSENT_KEY);
  const hasPreviouslyResponded = typeof savedOptIn === 'string';
  const hasConsented = hasPreviouslyResponded && savedOptIn === 'true';

  // disable CCPA consent bar (EO-350)
  isConsentRequired = false;

  if (isConsentRequired && !hasConsented) {
    uspString.setUserOptedOut(true);
  } else {
    uspString.markCCPADoesNotApply();
  }

  return {
    isConsentRequired,
    hasPreviouslyResponded,
    hasConsented,
    locationData: ipInfo,
  };
}

/**
 * Requests from the visitor consent to use cookies and other technologies to provide
 * personalized browsing experiences and advertisements.
 *
 * @returns {Promise<{consented: boolean}>}
 */
export function requestTrackingConsent() {
  // eslint-disable-next-line no-unused-vars
  return new Promise((resolve, reject) => {
    if (window.$ && window.$.fn && window.$.fn.jquery) {
      console.debug('Detected jQuery', {version: $.fn.jquery});
    } else {
      console.error('jQuery not detected, skipping consent request bar activation.');
      resolve(false);
      return;
    }

    $(() => {
      // show opt-in bar for user to choose
      const consentBarRoot = window.document.createElement('div');
      consentBarRoot.id = 'bt-cookies-consent';

      // Skip to main content does not exist on landers.
      const mainContentSkipLink = $('a[href*="#MainContent"]').get(0);

      if (typeof mainContentSkipLink !== 'undefined') {
        mainContentSkipLink.parentNode.insertBefore(consentBarRoot, mainContentSkipLink);
      } else {
        window.document.body.insertBefore(consentBarRoot, document.body.firstChild);
      }

      const onConsented = () => {
        uspString.setUserOptedOut(false);
        window.localStorage.setItem(CONSENT_KEY, 'true');
        BTCookies.set(CONSENT_KEY, true); // Shopify post purchase upsells does not have access to localstorage.
        resolve({consented: true});
      };

      ReactDOM.render(
        <CCPAConsentBar onConsented={onConsented} skipMainContentLink={mainContentSkipLink} />,
        consentBarRoot,
      );

      // Set initial focus on consent bar.
      $('#bt-consent-bar-container')[0].focus();

      trackersPush(trackers => {
        trackers.event('view', {category: 'view', name: 'view', label: 'CCPA Consent Bar'});
      });

      uspString.setExplicitNoticeProvided(true);
    });
  });
}
